import useStoreMetaOfferWhenLanding from "../../hooks/useStoreLandingMetaOffer.js";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "../../hooks/useReloadIfSearchStoreMissing.js";
import React, { Profiler } from "react";
import { useLocation, useParams } from "react-router-dom";
import { metaPreOptedAncillariesFromFlightDetailsScreenQueryToApiQuery } from "../../utils/preOptedAncillaries.js";
import { FlightDetailsInnerNewDesktop } from "../../components/elements/FlightDetailsInner/index.js";
import { POSMisMatchDesktop } from "../../components/elements/POSMisMatch/index.js";
import { useClientFetch } from "../../hooks/useClientFetch.js";
import useKayakS2SParams from "../../hooks/useKayakS2SParams.js";
import useReportBaggageQuality from "../../hooks/useReportBaggageInfoDiscrepancy.js";
import useFlightDetailsFromSearchResults from "../../hooks/useFlightDetailsFromSearchResults.js";
import useReportSRFDMismatch from "../../hooks/useReportSRFDMismatch.js";
import useLogFORAPErrors from "../../hooks/useLogFORAPErrors.js";
import useTrackFlightDetailsPriceChangeGoal from "../../hooks/useTrackFlightDetailsPriceChangeGoal.js";
import useTrackFlightDetailsBrandedFaresGoals from "../../hooks/useTrackFlightDetailsBrandedFaresGoals.js";
import useReportPriceDiscrepancy from "../../hooks/useReportPriceDiscrepancy.js";
import useTrackDeltaFlightDetailsGoal from "../../hooks/useTrackDeltaFlightDetailsGoal.js";
import useFlightOfferInSearchResults from "../../hooks/useFlightOfferInSearchResults.js";
import useAlternativeFaresParams from "../../hooks/useAlternativeFaresParams.js";
import useCaParamsCleanup from "../../hooks/useCaParamsCleanup.js";
import { isOfMetaOrigin } from "../../utils/marketing-url-params.js";
import { clientFetch } from "@flights/client-fetch";
import { CheckoutPaxDesktop } from "../../app/routes.js";
import NoSSR from "../../app/NoSSR/index.js";
import { HelmetHeader, useFlightDetailsDesktopController, useFlightDetailsDesktopApiUrl, useFlightDetailsDesktopEffects, useFlightDetailsDesktopEffectsAiSummary, useFlightDetailsDesktopEffectsPreload, useFlightDetailsDesktopEffectsCleanup } from "./index.js";
import useFlightDetailsAbortApiCallWeb from "./useFlightDetailsAbortApiCallWeb.js";
import { useSIRFTrackFlightDetails } from "../../hooks/useSIRFMessageTracking.js";
const _FlightDetailsNewDesktop = () => {
    const params = useParams();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const searchParams = metaPreOptedAncillariesFromFlightDetailsScreenQueryToApiQuery(urlParams);
    const landingMetaOffer = useStoreMetaOfferWhenLanding(params?.id);
    const { getTotalPrice } = useFlightOfferInSearchResults();
    const { setAlternativeFaresParams } = useAlternativeFaresParams();
    const cleanupCaParams = useCaParamsCleanup();
    const isMetaUser = isOfMetaOrigin();
    useReportBaggageQuality(params.id);
    useFlightDetailsDesktopEffects(params, location.search);
    const flightSR = useFlightDetailsFromSearchResults(params?.id);
    useReportSRFDMismatch(params?.id);
    useLogFORAPErrors(params.id);
    useTrackFlightDetailsPriceChangeGoal();
    useTrackFlightDetailsBrandedFaresGoals();
    const { flight, flightOffer, fetchStatus, onFetch, onFetchAbort, onFetchError, onFetchSuccess, posMismatch, backToSearch, onCloseWithTracking, handleErrorClose, trackRenderTime, store } = useFlightDetailsDesktopController(params, landingMetaOffer, location.search, flightSR);
    useSIRFTrackFlightDetails(flight);
    useKayakS2SParams(fetchStatus, flightSR, landingMetaOffer, searchParams, params);
    const resolvedFlight = (fetchStatus === "success" && store.flightDetails.flight) || flightOffer;
    useTrackDeltaFlightDetailsGoal(resolvedFlight || undefined);
    const { generateApiUrl, hasPrefetchedBrandedFareOffers } = useFlightDetailsDesktopApiUrl(params);
    const { units, nanos, currencyCode } = getTotalPrice(params?.id, urlParams.get("baseOfferToken") || "") || {};
    if (units) {
        const formattedPrice = `${units}-${nanos}-${currencyCode}`;
        setAlternativeFaresParams("priceInSearch", formattedPrice);
    }
    else {
        setAlternativeFaresParams("priceInSearch", "");
    }
    const url = generateApiUrl(urlParams, searchParams);
    const { signal, isAbortError } = useFlightDetailsAbortApiCallWeb(url);
    useClientFetch(url, {
        signal,
        condition: () => {
            return !isSearchStoreInitial(store.search);
        },
        fetch: onFetch,
        success: onFetchSuccess,
        error: (e) => {
            const error = e;
            if (isAbortError(e)) {
                onFetchAbort();
                return;
            }
            onFetchError(error);
        }
    });
    useFlightDetailsDesktopEffectsCleanup({ cleanupCaParams });
    useFlightDetailsDesktopEffectsPreload({ checkoutPaxDesktop: CheckoutPaxDesktop });
    useReportPriceDiscrepancy(params.id);
    const brandedFareIndex = urlParams.get("brandedFareIndex");
    useFlightDetailsDesktopEffectsAiSummary({
        brandedFareIndex,
        clientFetch,
        flightKey: flight?.flightKey,
        id: params?.id,
        isMetaUser,
        urlParams
    });
    if (useReloadIfSearchStoreMissing(store.search)) {
        return null;
    }
    return (React.createElement(Profiler, { id: "screen", onRender: trackRenderTime },
        React.createElement(NoSSR, null,
            React.createElement(HelmetHeader, null),
            fetchStatus === "success" && posMismatch && (React.createElement(POSMisMatchDesktop, { goToSearch: backToSearch, POSMismatch: posMismatch })),
            (fetchStatus === "loading" || fetchStatus == "failed" || (flight && !posMismatch)) && (React.createElement(FlightDetailsInnerNewDesktop, { flightToken: params?.id, flight: flight, fetchStatus: fetchStatus, onClose: onCloseWithTracking, onErrorClose: handleErrorClose, hasPrefetchedBrandedFareOffers: hasPrefetchedBrandedFareOffers })))));
};
const FlightDetailsNewDesktop = React.memo(_FlightDetailsNewDesktop);
export default FlightDetailsNewDesktop;
